/**
 * FogRenderer - Fog of War rendering class
 * Uses offscreen canvas composition to avoid destination-out affecting game elements
 */

import { FogOfWar } from './fogOfWar';
import { VISION_CONFIG, RadarSweepArea, VisionType } from './visionConfig';
import { PR } from '@/core/staticInitData';
import { packVisionSources } from '@/workers';
import type { RenderWorkerBridge, WorkerBitmapFrameMeta } from '@/workers';

/** Buffer expansion ratio: Canvas covers 1.5x viewport area to reduce rebuild frequency on camera movement */
const BUFFER_RATIO = 1.5;

export class FogRenderer {
    private _fog: FogOfWar;

    // Static fog layer (cached, only rebuild when vision sources change)
    private _staticCanvas: HTMLCanvasElement | null = null;
    private _staticCtx: CanvasRenderingContext2D | null = null;
    private _staticCacheValid: boolean = false;

    // Composite layer (rebuild each frame: static layer + radar holes)
    private _compositeCanvas: HTMLCanvasElement | null = null;
    private _compositeCtx: CanvasRenderingContext2D | null = null;
    private _dynamicDirty: boolean = true;

    // Cached world dimensions and pixel ratio
    private _cachedWidth: number = 0;
    private _cachedHeight: number = 0;
    private _cachedPR: number = 1;

    // Radar sweep area cache (for optimization: skip compose when changes are small)
    private _lastRadarAreas: RadarSweepArea[] = [];
    private _lastRadarCount: number = 0;

    // Pre-rendered vision hole mask (avoid creating RadialGradient every time)
    private _holeMaskCanvas: HTMLCanvasElement | null = null;
    private _holeMaskSize: number = 0;

    // Buffer-based viewport caching (viewport-level instead of world-level)
    private _lastCameraX: number = 0;
    private _lastCameraY: number = 0;
    private _lastZoom: number = 1;
    private _bufferLeft: number = 0;      // Buffer world area left boundary
    private _bufferTop: number = 0;       // Buffer world area top boundary
    private _bufferWorldWidth: number = 0;  // Buffer world width
    private _bufferWorldHeight: number = 0; // Buffer world height

    // Worker mode fields
    private _useWorker = false;             // Whether Worker mode is active
    private _workerBitmap: ImageBitmap | null = null;  // Latest bitmap from Worker
    private _bridge: RenderWorkerBridge | null = null; // Reference to bridge (set externally)

    constructor(fog: FogOfWar) {
        this._fog = fog;
    }

    /**
     * Mark static cache invalid (call when vision sources change)
     * Also sends a request to Worker if in Worker mode.
     */
    invalidateCache(): void {
        this._staticCacheValid = false;
        this._dynamicDirty = true;
        // Reset radar area cache when static vision changes
        this._lastRadarAreas = [];
        this._lastRadarCount = 0;

        // Request Worker rebuild if active
        if (this._useWorker && this._bridge?.isReady) {
            this._requestWorkerRebuild();
        }
    }

    /**
     * Enable Worker rendering mode.
     * Call after capability detection passes and bridge is ready.
     */
    enableWorkerMode(bridge: RenderWorkerBridge): void {
        this._bridge = bridge;
        this._useWorker = true;
        // Request initial rebuild from Worker
        if (bridge.isReady) {
            this._requestWorkerRebuild();
        }
    }

    /**
     * Disable Worker mode and fall back to main-thread rendering.
     * Call when Worker crashes or is disposed.
     */
    disableWorkerMode(): void {
        this._useWorker = false;
        this._bridge = null;
        this._closeWorkerBitmap();
        // Force main-thread rebuild on next render
        this._staticCacheValid = false;
    }

    /**
     * Receive new bitmap from Worker (called by bridge callback).
     * 使用 Worker 返回的真实 buffer 边界，避免主线程自行重算
     */
    setWorkerBitmap(bitmap: ImageBitmap, frameMeta: WorkerBitmapFrameMeta): void {
        this._closeWorkerBitmap();
        this._workerBitmap = bitmap;
        // Worker 模式下标记静态缓存有效，跳过主线程重建
        this._staticCacheValid = true;
        this._dynamicDirty = true;
        this._lastCameraX = frameMeta.cameraX;
        this._lastCameraY = frameMeta.cameraY;
        this._lastZoom = frameMeta.cameraZoom;
        this._bufferLeft = frameMeta.bufferLeft;
        this._bufferTop = frameMeta.bufferTop;
        this._bufferWorldWidth = frameMeta.bufferWorldWidth;
        this._bufferWorldHeight = frameMeta.bufferWorldHeight;
    }

    private _closeWorkerBitmap(): void {
        if (this._workerBitmap) {
            this._workerBitmap.close();
            this._workerBitmap = null;
        }
    }

    /**
     * Dispose resources including Worker bitmap.
     */
    dispose(): void {
        this._closeWorkerBitmap();
        this._staticCanvas = null;
        this._staticCtx = null;
        this._compositeCanvas = null;
        this._compositeCtx = null;
        this._holeMaskCanvas = null;
        this._bridge = null;
    }

    /**
     * Send a fog rebuild request to the Worker via bridge.
     */
    private _requestWorkerRebuild(): void {
        if (!this._bridge || !this._bridge.isReady) return;

        const world = this._fog.world;
        const camera = world.camera;
        const pr = PR;
        const viewWidth = world.viewWidth;
        const viewHeight = world.viewHeight;
        const canvasWidth = viewWidth * BUFFER_RATIO;
        const canvasHeight = viewHeight * BUFFER_RATIO;

        const { fogColor, outerGradientSize } = VISION_CONFIG;
        const sources = this._fog.getStaticVisionSources();
        const { buffer, count } = packVisionSources(sources);

        this._bridge.requestFogRebuild({
            canvasWidth,
            canvasHeight,
            pr,
            cameraX: camera.x,
            cameraY: camera.y,
            cameraZoom: camera.zoom,
            cameraViewWidth: camera.viewWidth,
            cameraViewHeight: camera.viewHeight,
            worldWidth: world.width,
            worldHeight: world.height,
            visionSourcesBuffer: buffer,
            visionSourcesCount: count,
            fogColorR: fogColor.r,
            fogColorG: fogColor.g,
            fogColorB: fogColor.b,
            fogColorA: fogColor.a,
            outerGradientSize,
        });
    }

    /**
     * Mark dynamic layer needs rebuild (call each frame for radar sweep)
     */
    markDynamicDirty(): void {
        this._dynamicDirty = true;
    }

    /**
     * Main render method
     *
     * Key design (viewport-level caching):
     * 1. Offscreen canvas covers viewport + buffer area (1.5x viewport)
     * 2. Camera movement triggers buffer rebuild when exceeding threshold
     * 3. Draw to world coordinates using 9-parameter drawImage
     *
     * Worker mode: When enabled, static fog layer is rendered by Worker and
     * received as ImageBitmap. Radar sweep effects are still rendered on main thread.
     */
    render(ctx: CanvasRenderingContext2D, worldWidth: number, worldHeight: number): void {
        if (!this._fog.enabled) return;

        const pr = PR;
        const world = this._fog.world;
        const viewWidth = world.viewWidth;
        const viewHeight = world.viewHeight;

        // Check pixel size change (viewport resize)
        const targetWidth = viewWidth * BUFFER_RATIO;
        const targetHeight = viewHeight * BUFFER_RATIO;

        if (targetWidth !== this._cachedWidth || targetHeight !== this._cachedHeight || pr !== this._cachedPR) {
            this._initCanvases(viewWidth, viewHeight, pr);
        }

        // Check if camera movement or zoom exceeds buffer range
        if (this._shouldRebuildForCamera()) {
            this._staticCacheValid = false;
        }

        // ----------- Worker path -----------
        if (this._useWorker) {
            // Request rebuild from Worker if cache is invalid
            if (!this._staticCacheValid && this._bridge?.isReady) {
                this._requestWorkerRebuild();
            }

            // Draw Worker bitmap (static layer) if available
            if (this._workerBitmap) {
                if (this._fog.hasRadarTowers()) {
                    // Compose radar sweep on top of Worker bitmap
                    if (this._dynamicDirty) {
                        this._composeFrame(this._cachedWidth, this._cachedHeight, pr, this._workerBitmap);
                        this._dynamicDirty = false;
                    }
                    if (this._compositeCanvas) {
                        ctx.drawImage(
                            this._compositeCanvas,
                            0, 0, this._cachedWidth * pr, this._cachedHeight * pr,
                            this._bufferLeft, this._bufferTop,
                            this._bufferWorldWidth, this._bufferWorldHeight
                        );
                    }
                } else {
                    // No radar: draw Worker bitmap directly
                    ctx.drawImage(
                        this._workerBitmap,
                        0, 0, this._workerBitmap.width, this._workerBitmap.height,
                        this._bufferLeft, this._bufferTop,
                        this._bufferWorldWidth, this._bufferWorldHeight
                    );
                }
                // Radar scan lines always drawn on main thread
                if (this._fog.hasRadarTowers()) {
                    this._renderRadarScanLines(ctx);
                }
                return;
            }
            // Bitmap not yet available — fall through to main-thread path
        }

        // ----------- Main-thread path (fallback) -----------

        // 1. Ensure static cache is valid
        if (!this._staticCacheValid) {
            this._rebuildStaticCache(this._cachedWidth, this._cachedHeight, pr);
        }

        // 2. Choose render path: with radar use composite layer, without use static directly
        // Draw to main canvas (using world coordinates and world size)
        if (this._fog.hasRadarTowers()) {
            // Compose final fog layer (static layer + radar sweep holes)
            if (this._dynamicDirty) {
                this._composeFrame(this._cachedWidth, this._cachedHeight, pr);
                this._dynamicDirty = false;
            }
            // Draw composed fog layer
            if (this._compositeCanvas) {
                ctx.drawImage(
                    this._compositeCanvas,
                    0, 0, this._cachedWidth * pr, this._cachedHeight * pr,
                    this._bufferLeft, this._bufferTop,
                    this._bufferWorldWidth, this._bufferWorldHeight
                );
            }
        } else {
            // [Performance] No radar towers: use static cache directly, skip composite copy
            if (this._staticCanvas) {
                ctx.drawImage(
                    this._staticCanvas,
                    0, 0, this._cachedWidth * pr, this._cachedHeight * pr,
                    this._bufferLeft, this._bufferTop,
                    this._bufferWorldWidth, this._bufferWorldHeight
                );
            }
        }
    }

    /**
     * Initialize offscreen canvases (viewport-level instead of world-level)
     */
    private _initCanvases(viewWidth: number, viewHeight: number, pr: number): void {
        // Canvas pixel size (not affected by zoom)
        const canvasWidth = viewWidth * BUFFER_RATIO;
        const canvasHeight = viewHeight * BUFFER_RATIO;

        this._cachedWidth = canvasWidth;
        this._cachedHeight = canvasHeight;
        this._cachedPR = pr;

        // Static cache canvas
        if (!this._staticCanvas) {
            this._staticCanvas = document.createElement('canvas');
            this._staticCtx = this._staticCanvas.getContext('2d');
        }
        this._staticCanvas.width = canvasWidth * pr;
        this._staticCanvas.height = canvasHeight * pr;

        // Composite canvas
        if (!this._compositeCanvas) {
            this._compositeCanvas = document.createElement('canvas');
            this._compositeCtx = this._compositeCanvas.getContext('2d');
        }
        this._compositeCanvas.width = canvasWidth * pr;
        this._compositeCanvas.height = canvasHeight * pr;

        // Initialize pre-rendered hole mask
        this._initHoleMask();

        this._staticCacheValid = false;
    }


    /**
     * Initialize pre-rendered vision hole mask
     * Creates a standard-size radial gradient mask that can be scaled when drawing
     * New: gradient extends outward from visible radius, creating 30% opacity fog at edge
     */
    private _initHoleMask(): void {
        const { outerGradientSize } = VISION_CONFIG;
        // Use a fixed size for the mask (outer gradient size determines effective radius)
        const maskRadius = outerGradientSize * 4;
        const size = (maskRadius + outerGradientSize) * 2;

        if (!this._holeMaskCanvas || this._holeMaskSize !== size) {
            this._holeMaskCanvas = document.createElement('canvas');
            this._holeMaskCanvas.width = size;
            this._holeMaskCanvas.height = size;
            this._holeMaskSize = size;

            const ctx = this._holeMaskCanvas.getContext('2d')!;
            const center = maskRadius + outerGradientSize;

            // Use a single radial gradient from center to outer edge
            // This creates smooth transition: center (full visible) -> edge (full fog)
            const gradient = ctx.createRadialGradient(
                center, center, 0,
                center, center, maskRadius + outerGradientSize
            );
            // Center: full alpha (complete fog removal)
            gradient.addColorStop(0, 'rgba(0,0,0,1)');
            // Inner edge of gradient zone: still mostly visible
            const gradientStart = maskRadius / (maskRadius + outerGradientSize);
            gradient.addColorStop(gradientStart, 'rgba(0,0,0,1)');
            // Outer edge: no fog removal (full fog)
            gradient.addColorStop(1, 'rgba(0,0,0,0)');

            ctx.beginPath();
            ctx.arc(center, center, maskRadius + outerGradientSize, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();
        }
    }

    /**
     * Check if camera movement or zoom requires buffer rebuild
     */
    private _shouldRebuildForCamera(): boolean {
        const camera = this._fog.world.camera;

        // Zoom change detection: rebuild when zoom changes significantly (either direction)
        // This ensures buffer world coverage matches current zoom level
        const zoomRatio = camera.zoom / this._lastZoom;
        if (zoomRatio < 0.9 || zoomRatio > 1.1) {
            return true;
        }

        // Position change detection
        const viewWorldWidth = camera.viewWidth / camera.zoom;
        const viewWorldHeight = camera.viewHeight / camera.zoom;
        const thresholdX = viewWorldWidth * 0.25;
        const thresholdY = viewWorldHeight * 0.25;

        const dx = Math.abs(camera.x - this._lastCameraX);
        const dy = Math.abs(camera.y - this._lastCameraY);

        return dx > thresholdX || dy > thresholdY;
    }

    /**
     * Rebuild static fog cache (viewport-level buffer)
     * On offscreen canvas: fill fog -> destination-out carve out static vision areas
     */
    private _rebuildStaticCache(canvasWidth: number, canvasHeight: number, pr: number): void {
        const ctx = this._staticCtx!;
        const camera = this._fog.world.camera;
        const world = this._fog.world;

        // Calculate viewport world size (affected by zoom)
        const viewWorldWidth = camera.viewWidth / camera.zoom;
        const viewWorldHeight = camera.viewHeight / camera.zoom;

        // Buffer covers world area
        this._bufferWorldWidth = viewWorldWidth * BUFFER_RATIO;
        this._bufferWorldHeight = viewWorldHeight * BUFFER_RATIO;

        // Calculate camera center (world coordinates)
        const cameraCenterX = camera.x + viewWorldWidth / 2;
        const cameraCenterY = camera.y + viewWorldHeight / 2;

        // Buffer world area boundaries (clipped to world bounds)
        this._bufferLeft = Math.max(0, cameraCenterX - this._bufferWorldWidth / 2);
        this._bufferTop = Math.max(0, cameraCenterY - this._bufferWorldHeight / 2);
        const bufferRight = Math.min(world.width, this._bufferLeft + this._bufferWorldWidth);
        const bufferBottom = Math.min(world.height, this._bufferTop + this._bufferWorldHeight);
        // Update world size based on actual clipped bounds
        this._bufferWorldWidth = bufferRight - this._bufferLeft;
        this._bufferWorldHeight = bufferBottom - this._bufferTop;

        // Coordinate transform: map world coordinates to canvas pixels
        // Scale factor = canvas pixel size / world size
        const scaleX = (canvasWidth * pr) / this._bufferWorldWidth;
        const scaleY = (canvasHeight * pr) / this._bufferWorldHeight;
        ctx.setTransform(scaleX, 0, 0, scaleY, -this._bufferLeft * scaleX, -this._bufferTop * scaleY);

        // Clear and fill fog
        ctx.clearRect(this._bufferLeft, this._bufferTop, this._bufferWorldWidth, this._bufferWorldHeight);
        const { fogColor } = VISION_CONFIG;
        ctx.fillStyle = `rgba(${fogColor.r}, ${fogColor.g}, ${fogColor.b}, ${fogColor.a})`;
        ctx.fillRect(this._bufferLeft, this._bufferTop, this._bufferWorldWidth, this._bufferWorldHeight);

        // Use destination-out to carve out visible areas (safe on offscreen canvas)
        ctx.globalCompositeOperation = 'destination-out';
        const sources = this._fog.getStaticVisionSources();

        // Only render vision sources within buffer range
        for (const src of sources) {
            if (src.x + src.radius < this._bufferLeft || src.x - src.radius > bufferRight ||
                src.y + src.radius < this._bufferTop || src.y - src.radius > bufferBottom) {
                continue;
            }
            this._drawVisionHole(ctx, src.x, src.y, src.radius);
        }

        ctx.globalCompositeOperation = 'source-over';

        // Record current camera state
        this._lastCameraX = camera.x;
        this._lastCameraY = camera.y;
        this._lastZoom = camera.zoom;
        this._staticCacheValid = true;

        // Static cache rebuilt with new buffer coordinates, composite frame must also update
        this._dynamicDirty = true;

        // Clear old radar data (buffer rebuild invalidates coordinates)
        this._lastRadarAreas = [];
    }

    /**
     * Check if radar sweep areas have changed significantly enough to require recomposition
     * Returns true if composition can be skipped (use previous frame's result)
     */
    private _shouldSkipCompose(areas: RadarSweepArea[], count: number): boolean {
        if (count !== this._lastRadarCount) return false;
        if (count === 0) return true; // No radar towers, use static cache

        const ANGLE_THRESHOLD = 0.01; // Angle change threshold (rad) ~0.57 degrees

        for (let i = 0; i < count; i++) {
            const current = areas[i];
            const last = this._lastRadarAreas[i];
            if (!last) return false;

            // Compare position and radius
            if (Math.abs(current.x - last.x) > 0.1 ||
                Math.abs(current.y - last.y) > 0.1 ||
                Math.abs(current.radius - last.radius) > 0.1) {
                return false;
            }

            // Compare angle (using center angle)
            const currentCenter = (current.startAngle + current.endAngle) / 2;
            const lastCenter = (last.startAngle + last.endAngle) / 2;
            const angleDiff = Math.abs(currentCenter - lastCenter);
            const normalizedDiff = Math.min(angleDiff, Math.PI * 2 - angleDiff);

            if (normalizedDiff > ANGLE_THRESHOLD) {
                return false;
            }
        }

        return true; // Changes are below threshold, can skip composition
    }

    /**
     * Compose each frame's final fog layer (using buffer coordinate system)
     * 1. Copy static cache (or Worker bitmap) to composite canvas
     * 2. Carve out radar sweep areas on composite canvas
     */
    private _composeFrame(canvasWidth: number, canvasHeight: number, pr: number, sourceBitmap?: ImageBitmap): void {
        const { areas, count } = this._fog.getRadarSweepAreas();

        // Check if composition can be skipped (optimization: avoid expensive canvas operations)
        if (this._shouldSkipCompose(areas, count)) {
            return; // Skip composition, reuse previous frame's result
        }

        const ctx = this._compositeCtx!;

        // Calculate scale factor (consistent with _rebuildStaticCache)
        const scaleX = (canvasWidth * pr) / this._bufferWorldWidth;
        const scaleY = (canvasHeight * pr) / this._bufferWorldHeight;

        // Always do full copy to ensure gradient integrity
        // (incremental update can corrupt gradients when radar overlaps static vision)
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvasWidth * pr, canvasHeight * pr);
        if (sourceBitmap) {
            // Worker mode: copy from ImageBitmap
            ctx.drawImage(sourceBitmap, 0, 0);
        } else if (this._staticCanvas) {
            // Main-thread mode: copy from offscreen canvas
            ctx.drawImage(this._staticCanvas, 0, 0);
        }

        // 2. Carve out current radar sweep areas on composite layer
        ctx.setTransform(scaleX, 0, 0, scaleY, -this._bufferLeft * scaleX, -this._bufferTop * scaleY);
        ctx.globalCompositeOperation = 'destination-out';

        const bufferRight = this._bufferLeft + this._bufferWorldWidth;
        const bufferBottom = this._bufferTop + this._bufferWorldHeight;

        for (let i = 0; i < count; i++) {
            const area = areas[i];
            // Skip radar areas outside buffer range
            if (area.x + area.radius < this._bufferLeft || area.x - area.radius > bufferRight ||
                area.y + area.radius < this._bufferTop || area.y - area.radius > bufferBottom) {
                continue;
            }
            this._drawRadarSweepHole(ctx, area);
        }

        ctx.globalCompositeOperation = 'source-over';

        // 3. Draw radar scan line effects (visual enhancement, doesn't affect fog)
        this._renderRadarScanLines(ctx);

        // 4. Update radar area cache for next frame's optimization
        // Snapshot values instead of sharing pooled objects (pooled objects mutate each frame)
        this._lastRadarAreas = [];
        for (let i = 0; i < count; i++) {
            const area = areas[i];
            this._lastRadarAreas.push({
                x: area.x,
                y: area.y,
                radius: area.radius,
                startAngle: area.startAngle,
                endAngle: area.endAngle,
                alpha: area.alpha
            });
        }
        this._lastRadarCount = count;
    }

    /**
     * Draw circular vision hole (with outer gradient edge)
     * New: gradient extends outward from visible radius
     */
    private _drawVisionHole(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
        const { outerGradientSize, innerEdgeFogOpacity } = VISION_CONFIG;

        // Use pre-rendered mask if available
        if (this._holeMaskCanvas && this._holeMaskSize > 0) {
            const maskRadius = outerGradientSize * 4;
            const scale = radius / maskRadius;
            const drawSize = this._holeMaskSize * scale;

            ctx.drawImage(
                this._holeMaskCanvas,
                x - drawSize / 2,
                y - drawSize / 2,
                drawSize,
                drawSize
            );
            return;
        }

        // Fallback: create directly (only if mask not initialized)
        // 1. Fill center area with full alpha (100% transparent / 0% fog)
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,1)';  // Full carve out
        ctx.fill();

        // 2. Draw gradient ring for edge transition (25% -> 100% fog)
        const innerAlpha = 1 - innerEdgeFogOpacity;  // 0.75 for 25% fog
        const gradient = ctx.createRadialGradient(
            x, y, radius,
            x, y, radius + outerGradientSize
        );
        gradient.addColorStop(0, `rgba(0,0,0,${innerAlpha})`);  // Inner edge: 25% fog
        gradient.addColorStop(1, 'rgba(0,0,0,0)');              // Outer edge: 100% fog

        ctx.beginPath();
        ctx.arc(x, y, radius + outerGradientSize, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
    }

    /**
     * Draw radar sweep sector hole (with gradient edge and alpha)
     */
    private _drawRadarSweepHole(ctx: CanvasRenderingContext2D, area: RadarSweepArea): void {
        const { edgeGradientRatio } = VISION_CONFIG;
        const edgeGradientSize = area.radius * edgeGradientRatio;

        const gradient = ctx.createRadialGradient(
            area.x, area.y, Math.max(0, area.radius - edgeGradientSize),
            area.x, area.y, area.radius
        );
        gradient.addColorStop(0, `rgba(0,0,0,${area.alpha})`);
        gradient.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.beginPath();
        ctx.moveTo(area.x, area.y);
        ctx.arc(area.x, area.y, area.radius, area.startAngle, area.endAngle);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
    }

    /**
     * Render radar scan line effects (visual enhancement)
     */
    private _renderRadarScanLines(ctx: CanvasRenderingContext2D): void {
        const towers = this._fog.world.batterys;

        ctx.save();
        ctx.globalCompositeOperation = 'source-over';

        for (const tower of towers) {
            if (!tower.inValidTerritory) continue;
            if (tower.visionType !== VisionType.RADAR || tower.visionLevel <= 0) continue;
            // Filter by player - only render scan lines for own towers
            if (this._fog.playerId !== null && tower.ownerId !== this._fog.playerId) continue;

            const radius = tower.getVisionRadius();

            // Draw scan line
            ctx.beginPath();
            ctx.moveTo(tower.pos.x, tower.pos.y);
            const endX = tower.pos.x + Math.cos(tower.radarAngle) * radius;
            const endY = tower.pos.y + Math.sin(tower.radarAngle) * radius;
            ctx.lineTo(endX, endY);
            ctx.strokeStyle = 'rgba(0, 255, 100, 0.8)';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Draw scan sector edge glow
            ctx.beginPath();
            ctx.moveTo(tower.pos.x, tower.pos.y);
            ctx.arc(
                tower.pos.x, tower.pos.y, radius,
                tower.radarAngle - VISION_CONFIG.radar.sweepAngle,
                tower.radarAngle + VISION_CONFIG.radar.sweepAngle
            );
            ctx.closePath();
            ctx.strokeStyle = 'rgba(0, 255, 100, 0.3)';
            ctx.lineWidth = 1;
            ctx.stroke();
        }
        ctx.restore();
    }
}
